/**
 * Result View Component
 * Displays quiz results and feedback with dashboard theme
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiXCircle, FiArrowLeft, FiClock, FiAward, FiEdit3, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import logger from '../../utils/logger';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 1.5rem;
  min-height: 100vh;
  
  @media (max-width: 1024px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
  
  /* Mobile viewport optimizations */
  @media (max-width: 768px) {
    /* Prevent horizontal scroll on mobile */
    overflow-x: hidden;
    /* Ensure proper touch scrolling */
    -webkit-overflow-scrolling: touch;
    /* Prevent zoom on input focus (iOS) */
    font-size: 16px;
    /* Safe area support for notched devices (iPhone X+ through iPhone 17 Pro Max) */
    padding-left: max(1rem, env(safe-area-inset-left));
    padding-right: max(1rem, env(safe-area-inset-right));
  }
  
  /* Large iPhone models (iPhone 15 Pro Max, iPhone 16 Pro Max, iPhone 17 Pro Max - 430px) */
  @media (max-width: 430px) {
    padding-left: max(0.875rem, env(safe-area-inset-left));
    padding-right: max(0.875rem, env(safe-area-inset-right));
  }
  
  @media (max-width: 480px) {
    /* Safe area support for smaller devices */
    padding-left: max(0.75rem, env(safe-area-inset-left));
    padding-right: max(0.75rem, env(safe-area-inset-right));
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid var(--primary-color);
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: var(--text-inverse);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: var(--transition);
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm);
  letter-spacing: 0.025em;

  &:hover {
    background: linear-gradient(135deg, var(--primary-hover) 0%, var(--primary-color) 100%);
    border-color: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: var(--shadow-sm);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin-bottom: 1.25rem;
    padding: 0.875rem 1.5rem;
    /* Ensure minimum touch target size for mobile */
    min-height: 44px;
    /* Better touch feedback */
    -webkit-tap-highlight-color: rgba(216, 184, 137, 0.2);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;
    gap: 0.5rem;
  }
`;

const FilterLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-right: 0.5rem;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
`;

const FilterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  background: ${props => props.$active ? 'var(--primary-color)' : 'white'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: ${props => props.$active ? 'var(--shadow-sm)' : 'none'};

  &:hover {
    border-color: var(--primary-color);
    background: ${props => props.$active ? 'var(--primary-color)' : 'var(--background-secondary)'};
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    min-width: calc(50% - 0.25rem);
  }
  
  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const RetakeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid var(--primary-color);
  background: var(--background-primary);
  color: var(--primary-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);

  &:hover {
    background: var(--primary-color);
    color: var(--text-inverse);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid var(--primary-color);
  background: var(--background-primary);
  color: var(--primary-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);

  &:hover {
    background: var(--primary-color);
    color: var(--text-inverse);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const Header = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 1024px) {
    padding: 1.5rem 1.5rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 1rem;
    text-align: center;
    margin-bottom: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem;
  }
`;


const HeaderMetadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
    gap: 0.75rem;
  }
`;

const QuizTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const QuizDescription = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  margin: 0;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    text-align: center;
  }
`;

const QuizDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem 1.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  line-height: 1.6;
  margin-top: 1rem;
  padding: 1.25rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    gap: 0.875rem;
  }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
  gap: 0.375rem;
    
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: baseline;
    gap: 1rem;
    padding-bottom: 0.875rem;
    border-bottom: 1px solid var(--border-color);
    
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
`;

const DetailLabel = styled.strong`
      font-weight: 600;
  color: var(--text-muted);
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    text-transform: none;
    letter-spacing: normal;
    color: var(--text-secondary);
    font-weight: 500;
    flex-shrink: 0;
    min-width: 90px;
  }
`;

const DetailValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    text-align: left;
    flex: 1;
    font-weight: 600;
    color: var(--text-primary);
    word-break: break-word;
  }
`;








const ResultsSection = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const SectionHeader = styled.div`
  padding: 1.5rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-light) 100%);
  }
  
  h2 {
    color: var(--text-primary);
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0;
    font-family: 'Inter', sans-serif;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      width: 3rem;
      height: 2px;
      background: var(--primary-color);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.25rem;
    
    h2 {
      font-size: 1rem;
      letter-spacing: 0.1em;
    }
  }
`;

const SectionContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const QuestionResult = styled(motion.div)`
  border: none;
  border-radius: 0;
  padding: 0;
  margin-bottom: 1.5rem;
  background: var(--background-primary);
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  overflow: hidden;

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: ${props => props.$correct 
    ? 'rgba(56, 161, 105, 0.05)' 
    : 'rgba(229, 62, 62, 0.05)'};
  border-bottom: none;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const QuestionPrompt = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  margin: 0;
  flex: 1;
  letter-spacing: -0.025em;
  line-height: 1.4;
  
  span {
    display: block;
  }
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const QuestionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  background: ${props => props.$correct ? 'var(--success-color)' : 'var(--error-color)'};
  color: var(--text-inverse);
  white-space: nowrap;
  box-shadow: var(--shadow-sm);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    align-self: flex-end;
    padding: 0.5rem 0.875rem;
    font-size: 0.6875rem;
  }
`;


const AnswerSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 1.5rem;
  background: var(--background-secondary);

  @media (max-width: 1024px) {
    gap: 1.25rem;
    padding: 1.25rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }
`;

const AnswerBox = styled.div`
  padding: 1.5rem;
  border-radius: 0;
  background: var(--background-primary);
  border: none;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
  position: relative;

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const AnswerLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.isCorrect ? 'var(--success-dark)' : 'var(--text-secondary)'};
  font-family: 'Inter', sans-serif;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '${props => props.isCorrect ? '✓' : '→'}';
    font-size: 0.875rem;
    font-weight: 700;
    color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--success-color)'};
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.isCorrect ? 'rgba(56, 161, 105, 0.1)' : 'rgba(56, 161, 105, 0.1)'};
    border-radius: 0;
  }
`;

const AnswerText = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  line-height: 1.6;
  padding: 1rem;
  background: var(--background-tertiary);
  border-radius: 0;
  border: none;
  min-height: 3rem;
  display: flex;
  align-items: center;
  transition: var(--transition);
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  
  &:hover {
    background: var(--background-quaternary);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.875rem;
    min-height: 2.75rem;
  }
`;

const CorrectAnswerText = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: var(--success-dark);
  font-family: 'Inter', sans-serif;
  line-height: 1.6;
  padding: 1rem;
  background: rgba(56, 161, 105, 0.05);
  border-radius: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  border: none;
  min-height: 3rem;
  display: flex;
  align-items: center;
  position: relative;
  transition: var(--transition);
  
  &:hover {
    background: rgba(56, 161, 105, 0.08);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.875rem;
    min-height: 2.75rem;
    /* Ensure text is readable on mobile */
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8125rem;
    min-height: 2.5rem;
  }
`;

const PointsDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.$correct ? 'var(--success-dark)' : 'var(--error-dark)'};
  font-family: 'Inter', sans-serif;
  padding: 1rem 1.5rem;
  background: ${props => props.$correct 
    ? 'rgba(56, 161, 105, 0.08)' 
    : 'rgba(229, 62, 62, 0.08)'};
  border-radius: 0;
  border-top: none;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    font-size: 0.6875rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-muted);
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  
  p {
    font-size: 1rem;
    font-weight: 500;
    margin-top: 1rem;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--error-color);
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--error-color);
  box-shadow: var(--shadow-sm);
  
  p {
    font-size: 1rem;
    font-weight: 500;
    margin-top: 1rem;
  }
`;

const NoFeedbackMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-muted);
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  font-family: 'Inter', sans-serif;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
  }
  
  p {
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    
    h3 {
      font-size: 1.125rem;
    }
    
    p {
      font-size: 0.9375rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    
    h3 {
      font-size: 1rem;
      margin-bottom: 0.75rem;
    }
    
    p {
      font-size: 0.875rem;
    }
  }
`;

const OverrideIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(216, 184, 137, 0.05);
  border: none;
  border-radius: 0;
  font-size: 0.8125rem;
  color: var(--primary-dark);
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  margin-bottom: 1rem;
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  
  &:hover {
    background: rgba(216, 184, 137, 0.08);
    box-shadow: var(--shadow-md);
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.6875rem;
  }
`;


function ResultView() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [permissions, setPermissions] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'correct', 'incorrect', 'incomplete'

  const fetchResults = useCallback(async () => {
    try {
      
      setLoading(true);
      setError(null);
      
      const response = await quizApiService.getAttemptResult(attemptId);
      
      
      if (response.success && response.data) {
        
        // Ensure quiz_metadata exists
        if (!response.data.quiz_metadata) {
          // CRITICAL FIX: If quiz_metadata is missing, try to construct it from available data
          // or set a more helpful error message
          logger.warn('Quiz metadata missing from API response', { attemptId, responseData: response.data });
          
          // If we have some data but missing metadata, try to use what we have
          if (response.data.score !== undefined || response.data.perQuestion) {
            // Construct minimal metadata from available data
            response.data.quiz_metadata = {
              quiz_id: null,
              title: 'Quiz',
              description: null,
              submitted_at: new Date().toISOString(),
              started_at: null,
              time_limit_minutes: null
            };
            logger.debug('Constructed minimal quiz_metadata from available data', { attemptId });
          } else {
            setError('Quiz metadata not available. Please try refreshing the page.');
            setLoading(false);
            return;
          }
        }
        
        setResults(response.data);
        setMetadataLoaded(true);
        setError(null);
      } else {
        const errorMessage = response.message || 'Failed to fetch results';
        setError(errorMessage);
        
        // Retry once if we haven't retried yet
        if (retryCount === 0) {
          setTimeout(() => {
            setRetryCount(1);
            // Use a fresh fetchResults call to avoid dependency issues
            quizApiService.getAttemptResult(attemptId).then((retryResponse) => {
              if (retryResponse.success && retryResponse.data && retryResponse.data.quiz_metadata) {
                setResults(retryResponse.data);
                setMetadataLoaded(true);
                setError(null);
              } else {
              }
            }).catch((retryErr) => {
            });
          }, 1000);
        } else {
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch results');
    } finally {
      setLoading(false);
    }
  }, [attemptId, retryCount]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await quizApiService.getAttemptPermissions(attemptId);
      if (response.success) {
        setPermissions(response.data);
      }
    } catch (err) {
      logger.error('Error fetching permissions', { error: err, attemptId });
    }
  }, [attemptId]);

  useEffect(() => {
    
    // Reset state when attemptId changes
    setResults(null);
    setLoading(true);
    setError(null);
    setMetadataLoaded(false);
    setRetryCount(0);
    setPermissions(null);
    
    // Check if results were passed from quiz submission
    if (location.state?.results) {
      const passedResults = location.state.results;
      
      // CRITICAL FIX: If results from location.state don't have quiz_metadata, fetch from API
      // This handles cases where the submit response structure differs from getAttemptResult
      if (passedResults.quiz_metadata) {
        setResults(passedResults);
        setMetadataLoaded(true);
        setLoading(false);
        fetchPermissions();
      } else {
        // Results passed but missing metadata - fetch from API to get complete data
        logger.debug('Results passed from submit but missing quiz_metadata, fetching from API', { attemptId });
        fetchResults();
        fetchPermissions();
      }
    } else {
      fetchResults();
      fetchPermissions();
    }
  }, [attemptId, location.state, fetchResults, fetchPermissions]);


  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={24} />
          <p>Loading results...</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <BackButton onClick={() => navigate('/student/quizzes')}>
          <FiArrowLeft size={16} />
          Back to Quizzes
        </BackButton>
        <ErrorState>
          <FiAlertCircle size={48} style={{ marginBottom: '1rem', color: 'var(--warning-color)' }} />
          <h3>Results Not Available Yet</h3>
          <p>{error}</p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            If you believe this is an error, please contact your instructor.
          </p>
        </ErrorState>
      </Container>
    );
  }

  if (!results) {
    return (
      <Container>
        <ErrorState>
          <p>No results found</p>
        </ErrorState>
      </Container>
    );
  }

  // Ensure quiz_metadata exists before rendering
  if (!results.quiz_metadata || !metadataLoaded) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={24} />
          <p>Loading quiz details...</p>
        </LoadingState>
      </Container>
    );
  }

  if (!results.immediate_feedback && !results.perQuestion) {
    return (
      <Container>
        <BackButton onClick={() => navigate('/student/quizzes')}>
          <FiArrowLeft size={16} />
          Back to Quizzes
        </BackButton>
        
        <NoFeedbackMessage>
          <FiAward size={48} />
          <h3>Quiz Submitted Successfully!</h3>
          <p>Your results will be available after the due date has passed.</p>
        </NoFeedbackMessage>
      </Container>
    );
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    logger.debug('formatDate called', { dateString });
    if (!dateString) return 'N/A';
    const formatted = new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    logger.debug('formatted date', { formatted });
    return formatted;
  };


  return (
    <Container>
      <BackButton onClick={() => navigate('/student/quizzes')}>
        <FiArrowLeft size={16} />
        Back to Quizzes
      </BackButton>


      <Header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <HeaderMetadata>
          <QuizTitle>{results.quiz_metadata?.quiz_title || 'Quiz Results'}</QuizTitle>
          {results.quiz_metadata?.quiz_description && (
            <QuizDescription>{results.quiz_metadata.quiz_description}</QuizDescription>
          )}
          <QuizDetails>
            {/* Row 1: Course, Instructor, Started, Completed */}
            {results.quiz_metadata?.course_name && (
              <DetailItem>
                <DetailLabel>Course</DetailLabel>
                <DetailValue>
                  {results.quiz_metadata.course_name} {results.quiz_metadata.course_code && `(${results.quiz_metadata.course_code})`}
                </DetailValue>
              </DetailItem>
            )}
            {results.quiz_metadata?.instructor_name && (
              <DetailItem>
                <DetailLabel>Instructor</DetailLabel>
                <DetailValue>{results.quiz_metadata.instructor_name}</DetailValue>
              </DetailItem>
            )}
            <DetailItem>
              <DetailLabel>Started</DetailLabel>
              <DetailValue>
                {(() => {
                  logger.debug('started_at', { started_at: results.quiz_metadata?.started_at });
                  return results.quiz_metadata?.started_at ? formatDate(results.quiz_metadata.started_at) : 'N/A';
                })()}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Completed</DetailLabel>
              <DetailValue>
                {(() => {
                  logger.debug('submitted_at', { submitted_at: results.quiz_metadata?.submitted_at });
                  return results.quiz_metadata?.submitted_at ? formatDate(results.quiz_metadata.submitted_at) : 'N/A';
                })()}
              </DetailValue>
            </DetailItem>
            
            {/* Row 2: Score, Total Correct, Total Incorrect */}
            <DetailItem>
              <DetailLabel>Score</DetailLabel>
              <DetailValue>
                {Math.round(results.score || 0)}/{Math.round(results.max_score || 0)} ({Math.round(((results.score || 0) / (results.max_score || 1)) * 100)}%)
                {results.quiz_metadata?.is_late && (
                  <span style={{ color: 'var(--warning-color)', marginLeft: '0.5rem', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                    (Late Submission)
                  </span>
                )}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Total Correct</DetailLabel>
              <DetailValue>
                {(() => {
                  // Calculate total correct from perQuestion results
                  if (results.perQuestion && Array.isArray(results.perQuestion)) {
                    const totalCorrect = results.perQuestion.filter(q => q.is_correct === true || q.is_correct === 1).length;
                    return totalCorrect;
                  }
                  // Fallback: calculate from score if perQuestion not available
                  return Math.round(results.score || 0);
                })()}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Total Incorrect</DetailLabel>
              <DetailValue>
                {(() => {
                  // Calculate total incorrect from perQuestion results
                  // Incorrect = questions with no answer OR questions answered incorrectly
                  if (results.perQuestion && Array.isArray(results.perQuestion)) {
                    const totalIncorrect = results.perQuestion.filter(q => {
                      // Check if question has an answer
                      const hasAnswer = q.your_answer !== null && 
                                       q.your_answer !== undefined && 
                                       q.your_answer !== '' &&
                                       !(Array.isArray(q.your_answer) && q.your_answer.length === 0);
                      
                      // Incorrect if: no answer provided OR answer provided but wrong
                      if (!hasAnswer) {
                        return true; // No answer = incorrect
                      }
                      
                      // Has answer but is wrong
                      return q.is_correct === false || q.is_correct === 0;
                    }).length;
                    return totalIncorrect;
                  }
                  // Fallback: calculate from score if perQuestion not available
                  const totalQuestions = Math.round(results.max_score || 0);
                  const totalCorrect = Math.round(results.score || 0);
                  return totalQuestions - totalCorrect;
                })()}
              </DetailValue>
            </DetailItem>
          </QuizDetails>
        </HeaderMetadata>
      </Header>

      {/* Filter Section */}
      {results.perQuestion && results.perQuestion.length > 0 && (
        <FilterContainer>
          <FilterLabel>Filter:</FilterLabel>
          <FilterButton 
            $active={filter === 'all'} 
            onClick={() => setFilter('all')}
          >
            All ({results.perQuestion.length})
          </FilterButton>
          <FilterButton 
            $active={filter === 'correct'} 
            onClick={() => setFilter('correct')}
          >
            Correct ({results.perQuestion.filter(q => q.is_correct === true || q.is_correct === 1).length})
          </FilterButton>
          <FilterButton 
            $active={filter === 'incorrect'} 
            onClick={() => setFilter('incorrect')}
          >
            Incorrect ({results.perQuestion.filter(q => {
              // Check if question has an answer
              const hasAnswer = q.your_answer !== null && 
                               q.your_answer !== undefined && 
                               q.your_answer !== '' &&
                               !(Array.isArray(q.your_answer) && q.your_answer.length === 0);
              
              // Incorrect if: no answer provided OR answer provided but wrong
              if (!hasAnswer) {
                return true; // No answer = incorrect
              }
              
              // Has answer but is wrong
              return q.is_correct === false || q.is_correct === 0;
            }).length})
          </FilterButton>
          <FilterButton 
            $active={filter === 'incomplete'} 
            onClick={() => setFilter('incomplete')}
          >
            Incomplete ({results.perQuestion.filter(q => {
              // Check if question has no answer provided
              const hasAnswer = q.your_answer !== null && 
                               q.your_answer !== undefined && 
                               q.your_answer !== '' &&
                               !(Array.isArray(q.your_answer) && q.your_answer.length === 0);
              return !hasAnswer;
            }).length})
          </FilterButton>
        </FilterContainer>
      )}

      {permissions && 
       results.quiz_metadata?.status !== 'graded' &&
       (permissions.allowRetakeAll || permissions.retakeQuestionIds.length > 0 || permissions.allowEditAll || permissions.editQuestionIds.length > 0) && (
        <ActionButtonsContainer>
          {(permissions.allowRetakeAll || permissions.retakeQuestionIds.length > 0) && (
            <RetakeButton onClick={() => navigate(`/student/attempt/${attemptId}?retake=true`)}>
              <FiRefreshCw size={16} />
              {permissions.allowRetakeAll ? 'Retake Entire Quiz' : `Retake ${permissions.retakeQuestionIds.length} Question${permissions.retakeQuestionIds.length > 1 ? 's' : ''}`}
            </RetakeButton>
          )}
          {(permissions.allowEditAll || permissions.editQuestionIds.length > 0) && (
            <EditButton onClick={() => navigate(`/student/attempt/${attemptId}?edit=true`)}>
              <FiEdit3 size={16} />
              {permissions.allowEditAll ? 'Edit All Answers' : `Edit ${permissions.editQuestionIds.length} Answer${permissions.editQuestionIds.length > 1 ? 's' : ''}`}
            </EditButton>
          )}
        </ActionButtonsContainer>
      )}

      {results.perQuestion && (
        <ResultsSection
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <SectionHeader>
            <h2>Question Review</h2>
          </SectionHeader>
          <SectionContent>
            {(() => {
              // Filter questions based on selected filter
              const filteredQuestions = results.perQuestion.filter(questionResult => {
                if (filter === 'all') return true;
                if (filter === 'correct') {
                  // Correct: must have is_correct === true AND have an answer
                  const hasAnswer = questionResult.your_answer !== null && 
                                   questionResult.your_answer !== undefined && 
                                   questionResult.your_answer !== '' &&
                                   !(Array.isArray(questionResult.your_answer) && questionResult.your_answer.length === 0);
                  return (questionResult.is_correct === true || questionResult.is_correct === 1) && hasAnswer;
                }
                if (filter === 'incorrect') {
                  // Incorrect: no answer provided OR answer provided but wrong
                  const hasAnswer = questionResult.your_answer !== null && 
                                   questionResult.your_answer !== undefined && 
                                   questionResult.your_answer !== '' &&
                                   !(Array.isArray(questionResult.your_answer) && questionResult.your_answer.length === 0);
                  
                  if (!hasAnswer) {
                    return true; // No answer = incorrect
                  }
                  
                  // Has answer but is wrong
                  return questionResult.is_correct === false || questionResult.is_correct === 0;
                }
                if (filter === 'incomplete') {
                  // Incomplete: only when user did not select any answer
                  const hasAnswer = questionResult.your_answer !== null && 
                                   questionResult.your_answer !== undefined && 
                                   questionResult.your_answer !== '' &&
                                   !(Array.isArray(questionResult.your_answer) && questionResult.your_answer.length === 0);
                  return !hasAnswer;
                }
                return true;
              });

              return filteredQuestions.map((questionResult, index) => (
                <QuestionResult 
                  key={questionResult.question_id} 
                  $correct={questionResult.is_correct}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + (index * 0.1), duration: 0.4 }}
                >
                  <QuestionHeader $correct={questionResult.is_correct}>
                    <QuestionPrompt>
                      <span>{index + 1}. {questionResult.question_prompt || 'Question text not available'}</span>
                    </QuestionPrompt>
                    <QuestionStatus $correct={questionResult.is_correct}>
                      {questionResult.is_correct ? (
                        <>
                          <FiCheckCircle size={16} />
                          Correct
                        </>
                      ) : (
                        <>
                          <FiXCircle size={16} />
                          Incorrect
                        </>
                      )}
                    </QuestionStatus>
                  </QuestionHeader>


                <AnswerSection>
                  {questionResult.override_info && (
                    <OverrideIndicator>
                      <FiEdit3 size={14} />
                      Answer overridden by {questionResult.override_info.override_by_name || 'instructor'}
                      {questionResult.override_info.instructor_override_reason && (
                        <span>: {questionResult.override_info.instructor_override_reason}</span>
                      )}
                    </OverrideIndicator>
                  )}
                  
                  <AnswerBox isCorrect={questionResult.is_correct}>
                    <AnswerLabel isCorrect={false}>Your Answer</AnswerLabel>
                    <AnswerText>
                      {(() => {
                        // Handle matching questions with array of pair objects
                        if (questionResult.question_type === 'matching' && Array.isArray(questionResult.your_answer)) {
                          if (questionResult.your_answer.length > 0 && questionResult.your_answer[0]?.left) {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {questionResult.your_answer.map((pair, index) => {
                                  const isCorrect = pair.isCorrect !== false; // Default to true if not specified
                                  
                                  return (
                                    <div key={index} style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      gap: '0.25rem',
                                      wordWrap: 'break-word', 
                                      overflowWrap: 'break-word'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {isCorrect ? (
                                          <FiCheckCircle size={18} style={{ color: 'var(--success-color)', flexShrink: 0 }} />
                                        ) : (
                                          <FiXCircle size={18} style={{ color: 'var(--error-color)', flexShrink: 0 }} />
                                        )}
                                        <div><strong>Term:</strong> {pair.left}</div>
                                      </div>
                                      <div><strong>Definition:</strong> {pair.right}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          return 'No answer provided';
                        }
                        
                        // Handle other question types
                        if (Array.isArray(questionResult.your_answer)) {
                          return questionResult.your_answer.length > 0 
                            ? questionResult.your_answer.join(', ') 
                            : 'No answer provided';
                        }
                        
                        return questionResult.your_answer && questionResult.your_answer.trim() !== '' 
                          ? questionResult.your_answer 
                          : 'No answer provided';
                      })()}
                    </AnswerText>
                  </AnswerBox>
                  
                  <AnswerBox isCorrect={true}>
                    <AnswerLabel isCorrect={true}>Correct Answer</AnswerLabel>
                    <CorrectAnswerText>
                      {(() => {
                        // Handle matching questions with array of pair objects
                        if (questionResult.question_type === 'matching' && Array.isArray(questionResult.correct_answer)) {
                          if (questionResult.correct_answer.length > 0 && questionResult.correct_answer[0]?.left) {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {questionResult.correct_answer.map((pair, index) => (
                                  <div key={index} style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '0.25rem',
                                    wordWrap: 'break-word', 
                                    overflowWrap: 'break-word' 
                                  }}>
                                    <div><strong>Term:</strong> {pair.left}</div>
                                    <div><strong>Definition:</strong> {pair.right}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return 'No correct answer available';
                        }
                        
                        // Handle fill_blank questions with array of answers
                        if (questionResult.question_type === 'fill_blank' && Array.isArray(questionResult.correct_answer)) {
                          return questionResult.correct_answer.map((answer, index) => (
                            <div key={index} style={{ marginBottom: '0.5rem' }}>
                              <strong>Blank Answer {index + 1}:</strong> {answer}
                            </div>
                          ));
                        }
                        
                        // Handle other question types
                        if (Array.isArray(questionResult.correct_answer)) {
                          return questionResult.correct_answer.join(', ');
                        }
                        
                        return questionResult.correct_answer || 'No correct answer available';
                      })()}
                    </CorrectAnswerText>
                  </AnswerBox>
                </AnswerSection>

                <PointsDisplay $correct={questionResult.is_correct}>
                  <FiAward size={16} />
                  {Math.round(questionResult.points_awarded || 0)} / {Math.round(questionResult.max_points || 0)} points
                </PointsDisplay>
              </QuestionResult>
              ))
            })()}
          </SectionContent>
        </ResultsSection>
      )}
      
      
    </Container>
  );
}

export default ResultView;
